import { CustomRolePermissionDto } from '../../../../../../api-client';
import React from 'react';
import { Flexbox, Tooltip, Typography } from '@hiiretail/synergy-ui';
import { AccessControl } from '../../../../../../common';
import { StyledButton, StyledPlusIcon } from './CustomRolePermissionAttributesRow.styles';
import { Chip } from '../../../../../../common/components/Chip/Chip';
import { RemoveAttributeFunction } from '../../../../hooks/useRemoveAttributeFacade';

type CustomRolePermissionAttributesRowProps = {
  permission: CustomRolePermissionDto;
  isFetchingAttributes: boolean;
  isRemovingAttribute: boolean;
  openAddAttributeModal: () => void;
  removeAttribute: RemoveAttributeFunction;
};

export type CustomRolePermissionAttributesRowComponent =
  React.FC<CustomRolePermissionAttributesRowProps>;

export const CustomRolePermissionAttributesRow: CustomRolePermissionAttributesRowComponent = ({
  permission,
  isFetchingAttributes,
  openAddAttributeModal,
  isRemovingAttribute,
  removeAttribute,
}) => {
  const attributes = React.useMemo(() => {
    return permission.attributes
      ? Object.entries(permission.attributes as Record<string, unknown>)
      : [];
  }, [permission.attributes]);

  const sliceAttribute = React.useCallback(
    (permission: CustomRolePermissionDto, attribute: string) => {
      const updatedPermission = { ...permission };

      if (updatedPermission.alias) {
        delete updatedPermission.alias;
      }

      if (updatedPermission.attributes) {
        const attributes = { ...updatedPermission.attributes } as Record<string, unknown>;
        if (attributes[attribute]) {
          delete attributes[attribute];
          updatedPermission.attributes = attributes;
        }
      }

      return updatedPermission;
    },
    [],
  );

  return (
    <Flexbox
      container
      alignItems="center"
      gutter={1}
      style={{ width: '100%', overflowY: 'auto', paddingBottom: '16px' }}
    >
      <>
        {attributes.length ? (
          <>
            {attributes.map((attribute) => (
              <Chip
                isDeleting={isRemovingAttribute}
                onDelete={() => removeAttribute(sliceAttribute(permission, attribute[0]))}
                text={`${attribute[0]}: ${attribute[1]}`}
                key={attribute[0]}
                backgroundColor="#7f7f7f33"
                permission="iam.custom-role.update"
              />
            ))}
          </>
        ) : (
          <Typography variant="body2">No resource bindings</Typography>
        )}
        <AccessControl
          permission="iam.custom-role.update"
          renderIfNoAccess={
            <Tooltip
              title={
                <Typography variant="caption" color={() => 'white'}>
                  You do not have permissions to perform this action.
                  <br />
                  Required permissions: {/*@ts-ignore*/}
                  <Typography variant="caption" weight="bold">
                    iam.custom-role.update
                  </Typography>
                </Typography>
              }
            >
              <StyledButton disabled data-testid={`disabled-add-attribute`}>
                <StyledPlusIcon size="md" />
              </StyledButton>
            </Tooltip>
          }
        >
          {permission.id.split('.')[0] !== 'pos' ? (
            <Tooltip
              title={
                <Typography variant="caption" color={() => 'white'}>
                  There is no available attributes for {permission.id}
                </Typography>
              }
            >
              <StyledButton disabled data-testid="no-attributes-button">
                <StyledPlusIcon size="md" />
              </StyledButton>
            </Tooltip>
          ) : (
            <StyledButton
              type="button"
              disabled={attributes.length >= 5 || isFetchingAttributes || isRemovingAttribute}
              onClick={openAddAttributeModal}
              data-testid="add-attribute"
            >
              <StyledPlusIcon size="md" />
            </StyledButton>
          )}
        </AccessControl>
      </>
    </Flexbox>
  );
};
